<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useTagEditor } from '@/composables/useTagEditor'
import { redundantImplications } from '@/settings/tagTips'
import { tagsPath } from '@/settings/tagsNav'
import { chipStyle } from '@/todos/tags'

// Hints for cleaning up the tags: a tag that adds a tag directly although another tag it adds already leads there. Removing the
// direct link changes nothing for the todos, since the chain still adds the tag.
const { t } = useI18n()
const editor = useTagEditor()
const tips = computed(() => redundantImplications(editor.tags.value))
</script>

<template>
  <div class="tag-tips">
    <p v-if="editor.error.value" class="error">{{ editor.error.value }}</p>
    <p v-if="editor.loading.value && !editor.loaded.value">{{ t('common.loading') }}</p>
    <UiGroup v-if="editor.loaded.value && !tips.length">
      <p class="ui-hint" data-testid="tips-none">{{ t('settings.tags.tips.none') }}</p>
    </UiGroup>
    <UiGroup v-for="tip in tips" :key="`${tip.tag.id}-${tip.implied.id}`" data-testid="tip">
      <UiList>
        <UiRow stacked>
          <i18n-t keypath="settings.tags.tips.redundant" tag="span" class="text">
            <template #tag
              ><span class="chip" :style="chipStyle(tip.tag.color)"># {{ tip.tag.name }}</span></template
            >
            <template #implied
              ><span class="chip" :style="chipStyle(tip.implied.color)"># {{ tip.implied.name }}</span></template
            >
            <template #via
              ><span v-for="(other, i) in tip.via" :key="other.id"
                >{{ i ? ', ' : '' }}<span class="chip" :style="chipStyle(other.color)"># {{ other.name }}</span></span
              ></template
            >
          </i18n-t>
        </UiRow>
        <UiRow>
          <button
            type="button"
            class="action"
            @click="editor.removeImplication(tip.tag, tip.implied.id)"
          >
            {{ t('settings.tags.tips.remove') }}
          </button>
          <RouterLink class="action" :to="tagsPath(tip.tag.id, 'implied')">
            {{ t('settings.tags.tips.open') }}
          </RouterLink>
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.chip {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  overflow-wrap: anywhere;
}

.text {
  line-height: 1.8;
}

.action {
  width: auto;
  padding: 0.1rem 0.4rem;
  margin-right: 0.8rem;
  background: none;
  border: none;
  color: var(--tg-link-color);
  font: inherit;
  cursor: pointer;
  text-decoration: none;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
